import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL, formatApiError } from '../../utils/api';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { ChatText, FloppyDisk, Trash, Plus, ArrowClockwise, CaretDown, CaretRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const FAMILIES = [
  ['winback', 'Win-back & retention'],
  ['activation', 'Activation'],
  ['loyalty', 'Loyalty & value'],
  ['broadcast', 'Broadcast'],
];
const FAMILY_LABEL = Object.fromEntries(FAMILIES);
const MERGE_TAGS = ['first_name', 'tier', 'balance', 'last_refill', 'days_since', 'refills', 'bottles', 'entity'];

export const CxTemplatesConfig = ({ bare = false } = {}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openKey, setOpenKey] = useState(null);
  const [savingKey, setSavingKey] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/cx/templates`, { withCredentials: true, headers: authHeaders() });
      setRows(data?.templates || []);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load templates'));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = (key, field, value) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, [field]: value } : r)));
  const toggleChannel = (key, ch) => setRows((rs) => rs.map((r) => {
    if (r.key !== key) return r;
    const set = new Set(r.channels || []);
    set.has(ch) ? set.delete(ch) : set.add(ch);
    return { ...r, channels: Array.from(set) };
  }));

  const save = async (r) => {
    setSavingKey(r.key);
    try {
      await axios.put(`${API_URL}/api/cx/templates/${r.key}`, {
        name: r.name, family: r.family, description: r.description || '',
        channels: r.channels || [], audience: r.audience || {},
        sms_body: r.sms_body || '', email_subject: r.email_subject || '', email_body: r.email_body || '',
        enabled: r.enabled !== false,
      }, { withCredentials: true, headers: authHeaders() });
      toast.success('Template saved');
    } catch (e) { toast.error(formatApiError(e, 'Save failed')); }
    finally { setSavingKey(''); }
  };

  const create = async () => {
    try {
      const { data } = await axios.post(`${API_URL}/api/cx/templates`, {
        name: 'New template', family: 'broadcast', channels: ['sms', 'email'],
        sms_body: 'Hi {first_name}, ...', email_subject: '', email_body: '',
      }, { withCredentials: true, headers: authHeaders() });
      await load();
      setOpenKey(data.key);
      toast.success('Template created');
    } catch (e) { toast.error(formatApiError(e, 'Create failed')); }
  };

  const remove = async (key) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API_URL}/api/cx/templates/${key}`, { withCredentials: true, headers: authHeaders() });
      await load();
      toast.success('Template deleted');
    } catch (e) { toast.error(formatApiError(e, 'Delete failed')); }
  };

  const reseed = async () => {
    if (!window.confirm('Restore the default templates? Existing seeds will be reset to defaults; your custom templates are kept.')) return;
    try {
      await axios.post(`${API_URL}/api/cx/templates/reseed`, {}, { withCredentials: true, headers: authHeaders() });
      await load();
      toast.success('Defaults restored');
    } catch (e) { toast.error(formatApiError(e, 'Reseed failed')); }
  };

  return (
    <div className={bare ? '' : 'bg-white border border-[#EDE6D8] rounded-lg p-6 mt-6'} data-testid="cx-templates-config">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ChatText size={20} className="text-[#0A2540]" />
          <div>
            <h3 className="text-lg font-semibold text-[#0A2540]">Message Templates</h3>
            <p className="text-xs text-[#78716C]">SMS &amp; email copy used by CX Campaigns. Merge tags like {'{first_name}'} auto-fill per customer.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={reseed} className="border-[#EDE6D8] rounded-lg h-9" data-testid="reseed-templates"><ArrowClockwise size={15} className="mr-1.5" />Restore defaults</Button>
          <Button onClick={create} className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg h-9" data-testid="new-template"><Plus size={15} className="mr-1.5" />New template</Button>
        </div>
      </div>

      {loading ? <p className="text-sm text-[#78716C]">Loading…</p> : (
        <div className="space-y-3">
          {FAMILIES.map(([fam, label]) => {
            const items = rows.filter((r) => r.family === fam);
            if (!items.length) return null;
            return (
              <div key={fam}>
                <p className="text-[10px] uppercase tracking-[0.15em] text-[#A8A29E] font-semibold mb-1.5">{label}</p>
                <div className="space-y-2">
                  {items.map((r) => {
                    const open = openKey === r.key;
                    return (
                      <div key={r.key} className="border border-[#EDE6D8] rounded-xl overflow-hidden" data-testid={`template-${r.key}`}>
                        <button onClick={() => setOpenKey(open ? null : r.key)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#FCFAF6]">
                          <span className="flex items-center gap-2 text-sm font-medium text-[#1C1917]">
                            {open ? <CaretDown size={15} /> : <CaretRight size={15} />}{r.name}
                            {r.enabled === false && <span className="text-[10px] px-1.5 py-0.5 rounded bg-stone-200 text-stone-500">disabled</span>}
                          </span>
                          <span className="flex gap-1">
                            {(r.channels || []).map((c) => <span key={c} className="text-[9px] px-1.5 py-0.5 rounded bg-[#EDE9E1] text-[#57534E] uppercase">{c}</span>)}
                          </span>
                        </button>
                        {open && (
                          <div className="px-4 pb-4 pt-1 space-y-3 border-t border-[#EDE6D8]">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs text-[#78716C]">Name</Label>
                                <Input value={r.name} onChange={(e) => patch(r.key, 'name', e.target.value)} className="border-[#EDE6D8] rounded-lg h-9 mt-1" data-testid={`tmpl-name-${r.key}`} />
                              </div>
                              <div>
                                <Label className="text-xs text-[#78716C]">Family</Label>
                                <Select value={r.family} onValueChange={(v) => patch(r.key, 'family', v)}>
                                  <SelectTrigger className="border-[#EDE6D8] rounded-lg h-9 mt-1"><SelectValue /></SelectTrigger>
                                  <SelectContent>{FAMILIES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                                </Select>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <label className="flex items-center gap-1.5 text-xs cursor-pointer"><Checkbox checked={(r.channels || []).includes('sms')} onCheckedChange={() => toggleChannel(r.key, 'sms')} data-testid={`tmpl-ch-sms-${r.key}`} />SMS</label>
                              <label className="flex items-center gap-1.5 text-xs cursor-pointer"><Checkbox checked={(r.channels || []).includes('email')} onCheckedChange={() => toggleChannel(r.key, 'email')} data-testid={`tmpl-ch-email-${r.key}`} />Email</label>
                              <label className="flex items-center gap-1.5 text-xs cursor-pointer ml-auto"><Checkbox checked={r.enabled !== false} onCheckedChange={(v) => patch(r.key, 'enabled', Boolean(v))} />Enabled</label>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {MERGE_TAGS.map((t) => <span key={t} className="px-1.5 py-0.5 rounded bg-[#FCFAF6] border border-[#EDE6D8] text-[10px] font-mono text-[#0A2540]">{`{${t}}`}</span>)}
                            </div>
                            <div>
                              <Label className="text-xs text-[#78716C]">SMS message</Label>
                              <Textarea value={r.sms_body || ''} onChange={(e) => patch(r.key, 'sms_body', e.target.value)} rows={2} className="border-[#EDE6D8] rounded-lg mt-1" data-testid={`tmpl-sms-${r.key}`} />
                            </div>
                            <div className="grid grid-cols-1 gap-3">
                              <div>
                                <Label className="text-xs text-[#78716C]">Email subject</Label>
                                <Input value={r.email_subject || ''} onChange={(e) => patch(r.key, 'email_subject', e.target.value)} className="border-[#EDE6D8] rounded-lg h-9 mt-1" data-testid={`tmpl-subject-${r.key}`} />
                              </div>
                              <div>
                                <Label className="text-xs text-[#78716C]">Email body</Label>
                                <Textarea value={r.email_body || ''} onChange={(e) => patch(r.key, 'email_body', e.target.value)} rows={4} className="border-[#EDE6D8] rounded-lg mt-1" data-testid={`tmpl-email-${r.key}`} />
                              </div>
                            </div>
                            <div className="flex justify-between pt-1">
                              <Button variant="ghost" onClick={() => remove(r.key)} className="text-[#B4453A] hover:bg-[#F6DDD9] rounded-lg h-9" data-testid={`tmpl-delete-${r.key}`}><Trash size={15} className="mr-1.5" />Delete</Button>
                              <Button onClick={() => save(r)} disabled={savingKey === r.key} className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg h-9" data-testid={`tmpl-save-${r.key}`}><FloppyDisk size={15} className="mr-1.5" />{savingKey === r.key ? 'Saving…' : 'Save'}</Button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
